import { appLocalStorage, isStorageKey } from '../utils/storage';
import { isDeletedOwner } from '../accountDeletion/ownerState';

export const GUEST_TRIAL_KEY = 'guest-trial:v1';
export const GUEST_VOTE_LIMIT = 5;
const TTL = 7 * 24 * 60 * 60 * 1000;
const INTENT_TTL = 30 * 60 * 1000;
// A 60,000-character article can occupy 240 KiB in UTF-8. Keep room for
// formatting and over-limit edits so a paste is never silently truncated.
export const GUEST_DRAFT_MAX_BYTES = 1024 * 1024;
export const GUEST_DRAFT_TOTAL_BYTES = 2 * 1024 * 1024;
const MAX_HTML = GUEST_DRAFT_MAX_BYTES;
const MAX_TOTAL = GUEST_DRAFT_TOTAL_BYTES;
const MAX_DRAFTS = 10;
const listeners = new Set();
let cachedRaw;
let state;
let revision = 0;
let storageAvailable = true;
let snapshot;
const validId = id => Number.isSafeInteger(Number(id)) && Number(id) > 0;
const validScore = score => Number.isInteger(Number(score)) && Number(score) >= 1 && Number(score) <= 7;
const bytes = text => new Blob([text]).size;
const empty = () => ({ version: 1, createdAt: Date.now(), votesUsed: 0, opinions: {}, intent: null });
const expired = timestamp => !Number.isFinite(timestamp) || Date.now() < timestamp || Date.now() - timestamp >= TTL;
const notify = () => {
  revision += 1;
  snapshot = undefined;
  listeners.forEach(listener => listener());
};

function decode(raw) {
  try {
    const value = JSON.parse(raw);
    if (!value || value.version !== 1 || expired(value.createdAt) || !value.opinions || typeof value.opinions !== 'object') return empty();
    const opinions = {};
    let total = 0;
    let drafts = 0;
    let votes = 0;
    Object.entries(value.opinions).slice(0, 15).forEach(([id, opinion]) => {
      if (isDeletedOwner(opinion?.claimedBy)) return;
      if (!validId(id) || !opinion || !validScore(opinion.score) || expired(opinion.updatedAt)) return;
      const voted = opinion.voted === true && votes < GUEST_VOTE_LIMIT;
      if (voted) votes += 1;
      const html = typeof opinion.html === 'string' ? opinion.html : '';
      const size = bytes(html);
      const keep = html && size <= MAX_HTML && total + size <= MAX_TOTAL && drafts < MAX_DRAFTS;
      if (keep) { total += size; drafts += 1; }
      if (voted || keep || !html) opinions[id] = { score: Number(opinion.score), html: keep ? html : '', voted,
        ...(voted ? { voteScore: validScore(opinion.voteScore) ? Number(opinion.voteScore) : Number(opinion.score) } : {}),
        ...(validId(opinion.claimedBy) ? { claimedBy: Number(opinion.claimedBy) } : {}), updatedAt: opinion.updatedAt };
    });
    const intent = value.intent;
    return { version: 1, createdAt: value.createdAt,
      votesUsed: Math.max(votes, Math.min(GUEST_VOTE_LIMIT, Number.isInteger(value.votesUsed) ? value.votesUsed : 0)), opinions,
      intent: intent && /^[a-zA-Z0-9-]{16,80}$/.test(intent.id || '') && validId(intent.storyId)
        && typeof intent.path === 'string' && Number.isFinite(intent.createdAt)
        && Date.now() >= intent.createdAt && Date.now() - intent.createdAt < INTENT_TTL
        ? { id: intent.id, storyId: Number(intent.storyId), path: intent.path, createdAt: intent.createdAt,
          ...(validId(intent.userId) ? { userId: Number(intent.userId) } : {}) } : null };
  } catch { return empty(); }
}

function read() {
  try {
    const raw = appLocalStorage.getItem(GUEST_TRIAL_KEY);
    // If storage rejected a write, keep the local in-memory trial usable.
    if (!state || (storageAvailable && raw !== cachedRaw)) {
      cachedRaw = raw;
      state = decode(raw);
      snapshot = undefined;
    }
  } catch { if (storageAvailable) snapshot = undefined; storageAvailable = false; }
  if (!state || expired(state.createdAt)) { state = empty(); snapshot = undefined; }
  return state;
}

function persist(next) {
  // A suspended tab may hold a pre-deletion snapshot. Never serialize its
  // closed owner's claimed drafts back into the shared guest-trial record.
  next = { ...next, opinions: Object.fromEntries(Object.entries(next.opinions)
    .filter(([, value]) => !isDeletedOwner(value.claimedBy))),
    intent: isDeletedOwner(next.intent?.userId) ? null : next.intent };
  state = next;
  const raw = JSON.stringify(next);
  try {
    appLocalStorage.setItem(GUEST_TRIAL_KEY, raw);
    storageAvailable = appLocalStorage.getItem(GUEST_TRIAL_KEY) === raw;
    if (storageAvailable) cachedRaw = raw;
  } catch { storageAvailable = false; }
  notify();
  return storageAvailable;
}

export function getGuestTrialSnapshot() {
  const current = read();
  if (!snapshot) snapshot = Object.freeze({ revision, votesUsed: current.votesUsed,
    remaining: GUEST_VOTE_LIMIT - current.votesUsed, limitReached: current.votesUsed >= GUEST_VOTE_LIMIT, storageAvailable });
  return snapshot;
}

function onStorage(event) {
  if (!isStorageKey(event, GUEST_TRIAL_KEY)) return;
  state = undefined; cachedRaw = undefined; storageAvailable = true;
  read(); notify();
}
export function subscribeGuestTrial(listener) {
  if (!listeners.size) window.addEventListener('storage', onStorage);
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (!listeners.size) window.removeEventListener('storage', onStorage);
  };
}

export function readGuestOpinion(storyId) {
  if (!validId(storyId)) return null;
  const opinion = read().opinions[Number(storyId)];
  return opinion && !opinion.claimedBy ? { score: opinion.score, html: opinion.html } : null;
}

export function recordGuestVote(storyId, score) {
  const current = read();
  const saved = current.opinions[Number(storyId)];
  const old = saved?.claimedBy ? null : saved;
  const same = Number(score) === (old?.voteScore ?? old?.score ?? 4);
  if (!validId(storyId) || !validScore(score) || (!same && current.votesUsed >= GUEST_VOTE_LIMIT)) {
    return { accepted: false, changed: false, limitReached: current.votesUsed >= GUEST_VOTE_LIMIT,
      opinion: readGuestOpinion(storyId), storageAvailable };
  }
  if (!same) persist({ ...current, votesUsed: current.votesUsed + 1,
    intent: saved?.claimedBy && current.intent?.storyId === Number(storyId) ? null : current.intent,
    opinions: { ...current.opinions,
    [Number(storyId)]: { ...old, score: Number(score), voteScore: Number(score), html: old?.html || '', voted: true, updatedAt: Date.now() } } });
  else if (old && old.score !== Number(score)) persist({ ...current, opinions: { ...current.opinions,
    [Number(storyId)]: { ...old, score: Number(score), updatedAt: Date.now() } } });
  return { accepted: true, changed: !same, limitReached: read().votesUsed >= GUEST_VOTE_LIMIT,
    opinion: readGuestOpinion(storyId) || { score: Number(score), html: '' }, storageAvailable };
}

export function saveGuestDraft(storyId, html, score = 4) {
  const current = read();
  if (!validId(storyId) || !validScore(score) || typeof html !== 'string' || bytes(html) > MAX_HTML) {
    return { saved: false, storageAvailable, opinion: readGuestOpinion(storyId) };
  }
  const opinions = { ...current.opinions };
  const claimed = Boolean(opinions[Number(storyId)]?.claimedBy);
  opinions[Number(storyId)] = { ...(claimed ? {} : opinions[Number(storyId)]), html, score: Number(score), updatedAt: Date.now() };
  const others = Object.keys(opinions).filter(id => Number(id) !== Number(storyId))
    .sort((a, b) => opinions[a].updatedAt - opinions[b].updatedAt);
  const tooLarge = () => Object.keys(opinions).length > 15 || Object.values(opinions).filter(item => item.html).length > MAX_DRAFTS
    || Object.values(opinions).reduce((sum, item) => sum + bytes(item.html || ''), 0) > MAX_TOTAL;
  while (tooLarge() && others.length) {
    const id = others.shift();
    if (opinions[id].voted) opinions[id] = { ...opinions[id], html: '' };
    else delete opinions[id];
  }
  persist({ ...current, opinions, intent: claimed && current.intent?.storyId === Number(storyId) ? null : current.intent });
  return { saved: true, storageAvailable, opinion: readGuestOpinion(storyId) };
}

export function safeAuthReturn(path) {
  if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//') || path.includes('\\')
    || [...path].some(char => char.charCodeAt(0) < 32)) return '/';
  return path;
}

// Return intent travels in the existing OAuth `from` path, never the draft.
// A login initiated elsewhere cannot silently claim a visitor's private text.
export function prepareGuestAuth(storyId, returnPath) {
  const safe = safeAuthReturn(returnPath);
  const target = new URL(safe, 'https://wiw.invalid');
  if (!validId(storyId) || !new RegExp(`^/(?:[a-z]{2}/)?news/${Number(storyId)}/opinion$`).test(target.pathname)) return safe;
  const id = window.crypto?.randomUUID?.() || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
  target.searchParams.set('guestDraft', id);
  const path = target.pathname + target.search + target.hash;
  const current = read();
  const opinions = { ...current.opinions };
  // A completed sign-in owns its source text even if cleanup was interrupted.
  // A later visitor can start a fresh intent, never reclaim that account's text.
  if (opinions[Number(storyId)]?.claimedBy) delete opinions[Number(storyId)];
  persist({ ...current, opinions, intent: { id, storyId: Number(storyId), path, createdAt: Date.now() } });
  return path;
}

export function claimGuestAuth(path, userId) {
  if (isDeletedOwner(userId)) return false;
  const current = read();
  const intent = current.intent;
  if (!validId(userId) || !intent || intent.path !== path || intent.userId
    || Date.now() - intent.createdAt >= INTENT_TTL) return false;
  const opinion = current.opinions[intent.storyId];
  if (opinion?.claimedBy && opinion.claimedBy !== Number(userId)) return false;
  persist({ ...current, intent: { ...intent, userId: Number(userId) }, opinions: { ...current.opinions,
    ...(opinion ? { [intent.storyId]: { ...opinion, claimedBy: Number(userId) } } : {}) } });
  return true;
}

export function readGuestHandoff(storyId, userId, path) {
  if (isDeletedOwner(userId)) return null;
  const current = read();
  const intent = current.intent;
  if (!intent || intent.path !== path || intent.storyId !== Number(storyId) || intent.userId !== Number(userId)
    || Date.now() - intent.createdAt >= INTENT_TTL) return null;
  const opinion = current.opinions[intent.storyId];
  return opinion?.claimedBy === Number(userId) ? { id: intent.id, score: opinion.score, html: opinion.html } : null;
}

export function completeGuestHandoff(id) {
  const current = read();
  if (current.intent?.id !== id) return;
  const opinions = { ...current.opinions };
  const old = opinions[current.intent.storyId];
  if (old?.voted) opinions[current.intent.storyId] = { ...old, html: '' };
  else delete opinions[current.intent.storyId];
  persist({ ...current, opinions, intent: null });
}

export function forgetClaimedGuestDrafts(userId) {
  const current = read();
  const opinions = Object.fromEntries(Object.entries(current.opinions).filter(([, value]) => value.claimedBy !== Number(userId)));
  const intent = current.intent?.userId === Number(userId) ? null : current.intent;
  // decode() can already hide a closed owner's values from a cold read. Still
  // persist the cleaned record, otherwise the original bytes remain on disk.
  return persist({ ...current, opinions, intent });
}
